import type { ControllerOptions, WebController } from "@wishlink/applens-web";
import type { ScreenCompletion } from "@wishlink/applens-core";
import type {
  Clock,
  HomeHandle,
  HomeState,
  LocalConfig,
  DeploymentConfig,
} from "./contracts";
import { safely } from "./contracts";
import { API_NAMES } from "./registry";
import { createDependencies, HOME_TERMINAL_SCHEMA } from "./dependencies";
import type { DependencyTimings } from "./dependencies";

export const HOME_ERRORS = [
  { code: "home.blocking", category: "application" as const },
  { code: "home.partial", category: "application" as const },
];
export function homeCompletion(state: HomeState): ScreenCompletion | undefined {
  if (state.redirecting) return { outcome: "cancelled" };
  if (state.blockingError)
    return { outcome: "full_error", errorCode: "home.blocking" };
  if (
    state.configReady &&
    state.creatorReady &&
    state.branchCommitted &&
    !state.socialPending
  )
    return state.partialError
      ? { outcome: "degraded", errorCode: "home.partial" }
      : { outcome: "ready" };
  return undefined;
}
export function isLoopback(origin: string) {
  return safely(() => {
    const url = new URL(origin);
    return (
      ["http:", "https:"].includes(url.protocol) &&
      ["localhost", "127.0.0.1", "[::1]"].includes(url.hostname) &&
      url.origin === origin
    );
  }, false);
}
interface Dependencies {
  mode?: ControllerOptions["mode"];
  deploymentEndpoint?: ControllerOptions["destination"];
  clock: Clock;
  origin: string;
  bootstrapDeadline?: number;
  allowDeploymentPropagation?: boolean;
  loadBinding?: ControllerOptions["loadBinding"];
  schedule: ControllerOptions["schedule"];
  defer(work: () => void): void;
  lifecycle?: ControllerOptions["lifecycle"];
}
/** Creator composition: readiness, Strict Mode reference counting and RTK snapshots. */
export function createController(deps: Dependencies) {
  const mode = deps.mode ?? (isLoopback(deps.origin) ? "local" : "deployment");
  const destination = deps.deploymentEndpoint && { ...deps.deploymentEndpoint };
  const deploymentPropagation = deps.allowDeploymentPropagation === true;
  let sdk: WebController | undefined;
  let attachHeaders:
    | typeof import("@wishlink/applens-web/requests").attachRequestHeaders
    | undefined;
  let generation = -1;
  let identity: string | undefined;
  let normalizeIdentity:
    | typeof import("@wishlink/applens-core").normalizeIdentity
    | undefined;
  let config: Readonly<LocalConfig> | undefined;
  let revision = 0;
  let suspended = false;
  let readDependencies: () => DependencyTimings = () => ({});
  type Navigation = {
    handle: HomeHandle;
    references: number;
    state: HomeState;
    pending?: boolean;
    closed: boolean;
    dependencies: ReturnType<typeof createDependencies>;
  };
  let navigation: Navigation | undefined;
  const active = () => {
    if (config && deps.clock.epochNow() >= config.expiresAt) disable();
    return !suspended && config ? config : undefined;
  };
  function disable() {
    revision++;
    config = undefined;
    sdk?.disable();
  }
  async function configure(
    input: LocalConfig | DeploymentConfig,
    requestedMode: "local" | "deployment"
  ) {
    const nextGeneration = safely(() => input.generation, NaN);
    if (!Number.isSafeInteger(nextGeneration) || nextGeneration <= generation) {
      disable();
      return false;
    }
    generation = nextGeneration;
    disable();
    suspended = false;
    const expected = revision;
    // OFF/excluded activation does not fetch even the portable SDK control plane.
    if (!sdk && (input.enabled !== true || input.eligible !== true))
      return false;
    if (!sdk) {
      let loaded: [
        typeof import("@wishlink/applens-web"),
        typeof import("@wishlink/applens-web/requests"),
        typeof import("@wishlink/applens-core")
      ];
      try {
        loaded = await Promise.all([
          import(
            /* webpackChunkName: "applens-control" */ "@wishlink/applens-web"
          ),
          import(
            /* webpackChunkName: "applens-requests" */ "@wishlink/applens-web/requests"
          ),
          import(
            /* webpackChunkName: "applens-core" */ "@wishlink/applens-core"
          ),
        ]);
      } catch {
        return false;
      }
      const [module, requests, core] = loaded;
      if (expected !== revision) return false;
      attachHeaders = requests.attachRequestHeaders;
      normalizeIdentity = core.normalizeIdentity;
      const normalized = normalizeIdentity(input.identity);
      if (
        !normalized ||
        normalized.runtime !== "web" ||
        !(mode === "local"
          ? normalized.environment === "local"
          : ["staging", "production"].includes(normalized.environment))
      )
        return false;
      identity = JSON.stringify(normalized);
      sdk = module.createWebController({
        mode,
        ...(mode === "deployment"
          ? { destination, allowDeploymentPropagation: deploymentPropagation }
          : {}),
        identity: normalized,
        origin: deps.origin,
        clock: deps.clock,
        schedule: deps.schedule,
        loadBinding: deps.loadBinding,
        instrumentationName: "wishlink-creator",
        endpoints: API_NAMES.map((name) => ({ name })),
        errors: HOME_ERRORS,
        bootstrap: {
          timeoutMs: Math.max(
            1,
            Math.min(
              60000,
              (deps.bootstrapDeadline ?? Infinity) - deps.clock.now()
            )
          ),
          maxStarts: 64,
          taxonomy: { product: "creator", feature: "navigation", flow: "home" },
        },
        healthScreens: ["home"],
        screens: [
          {
            routeId: "home",
            taxonomy: {
              product: "creator",
              feature: "navigation",
              flow: "home",
            },
            terminalAttributes: HOME_TERMINAL_SCHEMA,
            readTerminalAttributes() {
              if (!navigation) return undefined;
              navigation.closed = true;
              return {
                ...navigation.dependencies(safely(readDependencies, {})),
              };
            },
          },
        ],
        lifecycle: deps.lifecycle && {
          listen(hidden, visible) {
            return deps.lifecycle!.listen(
              () => {
                suspended = true;
                if (navigation) navigation.closed = true;
                hidden();
              },
              () => {
                suspended = false;
                visible();
              }
            );
          },
        },
      });
    }
    const permitted =
      requestedMode === mode &&
      identity === JSON.stringify(normalizeIdentity!(input.identity)) &&
      (mode !== "local" ||
        (input as LocalConfig).exporterPath === "/client/v1/traces");
    const enabled = await sdk.configure(
      permitted ? input : { ...input, enabled: false }
    );
    if (!enabled || expected !== revision) return false;
    config = Object.freeze({
      ...input,
      apiOrigins: Object.freeze([...input.apiOrigins]),
    }) as Readonly<LocalConfig>;
    return true;
  }
  // Optional until beta.2 is published and consumed in its own exact-version PR.
  function renewLease(input: DeploymentConfig): boolean {
    if (
      !active() ||
      !sdk ||
      !config ||
      input.generation <= generation ||
      identity !== JSON.stringify(normalizeIdentity!(input.identity))
    )
      return false;
    const renewable = sdk as WebController & {
      renewLease?: (activation: DeploymentConfig) => boolean;
    };
    if (!renewable.renewLease?.(input)) return false;
    generation = input.generation;
    config = Object.freeze({
      ...config,
      generation,
      expiresAt: input.expiresAt,
    });
    return true;
  }
  function enter(key: string, isHome: boolean): HomeHandle {
    if (navigation?.handle.key === key) {
      navigation.references++;
      return navigation.handle;
    }
    safely(() => navigation?.handle.finish({ outcome: "cancelled" }), false);
    const expected = revision;
    const record: Navigation = {
      handle: undefined as unknown as HomeHandle,
      references: 1,
      state: {},
      closed: !isHome || !active(),
      dependencies: createDependencies(
        deps.clock.epochNow(),
        safely(readDependencies, {})
      ),
    };
    navigation = record;
    const attempt = sdk?.startScreen(isHome ? "home" : "other", key);
    const root = !record.closed
      ? {
          get closed() {
            return record.closed || revision !== expected || !active();
          },
        }
      : undefined;
    const handle: HomeHandle = {
      key,
      root,
      finish: (completion) =>
        safely(() => attempt?.finish(completion) ?? false, false),
      report(state) {
        if (navigation !== record || !root || root.closed) return;
        record.state = { ...record.state, ...state };
        if (record.pending) return;
        record.pending = true;
        deps.defer(() => {
          record.pending = false;
          if (navigation !== record || root.closed) return;
          const completion = homeCompletion(record.state);
          if (completion) handle.finish(completion);
        });
      },
    };
    record.handle = handle;
    return handle;
  }
  return {
    configure: (input: LocalConfig) => configure(input, "local"),
    configureDeployment: (input: DeploymentConfig) =>
      configure(input, "deployment"),
    renewLease,
    setDependencyReader(reader: () => DependencyTimings) {
      readDependencies = reader;
    },
    attachRequestHeaders: (
      request: Request,
      observation?: Parameters<NonNullable<typeof attachHeaders>>[1]
    ) => attachHeaders?.(request, observation) ?? request,
    captureRequest: (name: string) =>
      !navigation?.handle.root &&
      deps.clock.now() >= (deps.bootstrapDeadline ?? Infinity)
        ? undefined
        : sdk?.captureRequest(name),
    startRequest: (...args: Parameters<WebController["startRequest"]>) =>
      sdk?.startRequest(...args),
    exportHealth: () =>
      sdk?.exportHealth() ?? {
        disabled: { batches: 0, spans: 0 },
        busy: { batches: 0, spans: 0 },
        serialization: { batches: 0, spans: 0 },
        failed: { batches: 0, spans: 0 },
      },
    disable,
    enter,
    leave(handle: HomeHandle) {
      if (navigation?.handle !== handle) return;
      const record = navigation;
      record.references--;
      deps.defer(() => {
        if (record.references <= 0) handle.finish({ outcome: "cancelled" });
      });
    },
    captureHome: () =>
      navigation?.handle.root && !navigation.handle.root.closed
        ? navigation.handle
        : undefined,
    activeConfig: active,
  };
}
export type Controller = ReturnType<typeof createController>;
